import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { HardHat, Phone, Check, ArrowRight, MapPin } from 'lucide-react';

export default function TravauxFondations() {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';
  const { API } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    nom: '', entreprise: '', telephone: '', email: '', type_travaux: '',
    localisation_chantier: '', volume_description: '', delai_souhaite: '', budget: '', rgpd_accepted: false,
  });

  const typesTravaux = [
    { value: 'micropieux', label: isFr ? 'Micropieux' : 'Micropiles' },
    { value: 'pieux', label: isFr ? 'Pieux forés / Pieux battus' : 'Bored/Driven piles' },
    { value: 'injection', label: isFr ? 'Injection / Traitement de sol' : 'Grouting / Soil treatment' },
    { value: 'jet_grouting', label: 'Jet grouting' },
    { value: 'tirants', label: isFr ? 'Tirants d\'ancrage' : 'Tie-back anchors' },
    { value: 'paroi', label: isFr ? 'Paroi moulée / Berlinoise' : 'Diaphragm wall / Sheet piling' },
    { value: 'renforcement', label: isFr ? 'Renforcement de fondations' : 'Foundation reinforcement' },
    { value: 'autre', label: isFr ? 'Autre' : 'Other' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.rgpd_accepted) { toast.error(isFr ? 'Veuillez accepter les conditions' : 'Please accept'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.keys(formData).forEach(k => { if (formData[k] !== '' && formData[k] !== null) fd.append(k, formData[k]); });
      await axios.post(`${API}/demandes/travaux`, fd);
      setSubmitted(true);
    } catch (error) { toast.error(isFr ? 'Erreur' : 'Error'); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><Check className="w-10 h-10 text-green-600" /></div>
          <h1 className="text-3xl font-bold text-[#0A2540] mb-4">{isFr ? 'Merci !' : 'Thank you!'}</h1>
          <p className="text-gray-600 mb-6">{isFr ? 'Nous contactons les entreprises spécialisées. Devis sous 48-72h.' : 'We\'re contacting specialized companies. Quote within 48-72h.'}</p>
          <div className="flex items-center justify-center gap-2 text-[#635BFF] font-medium"><Phone className="w-5 h-5" /><span>+33 7 82 93 93 83</span></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="pt-16 pb-8 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-amber-100 rounded-full px-4 py-2 mb-6">
            <HardHat className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-600">{isFr ? 'Travaux de fondation' : 'Foundation works'}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-4">{isFr ? 'Demandez un devis travaux' : 'Request a works quote'}</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{isFr ? 'Micropieux, jet grouting, ancrages, parois... On vous met en relation avec les meilleures entreprises.' : 'Micropiles, jet grouting, anchors, walls... We connect you with the best contractors.'}</p>
        </div>
      </section>

      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-2xl">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>{isFr ? 'Nom complet' : 'Full name'} *</Label><Input required value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} className="mt-2" /></div>
                <div><Label>{isFr ? 'Entreprise' : 'Company'}</Label><Input value={formData.entreprise} onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })} className="mt-2" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>{isFr ? 'Téléphone' : 'Phone'} *</Label><Input type="tel" required value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} className="mt-2" /></div>
                <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-2" /></div>
              </div>
              <div>
                <Label>{isFr ? 'Type de travaux' : 'Type of works'} *</Label>
                <Select value={formData.type_travaux} onValueChange={(v) => setFormData({ ...formData, type_travaux: v })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder={isFr ? 'Sélectionnez' : 'Select'} /></SelectTrigger>
                  <SelectContent>{typesTravaux.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>{isFr ? 'Localisation chantier' : 'Site location'} *</Label><Input required value={formData.localisation_chantier} onChange={(e) => setFormData({ ...formData, localisation_chantier: e.target.value })} placeholder="69001 Lyon" className="mt-2" /></div>
              <div><Label>{isFr ? 'Volume / Description des travaux' : 'Volume / Description'} *</Label><Textarea required value={formData.volume_description} onChange={(e) => setFormData({ ...formData, volume_description: e.target.value })} placeholder={isFr ? 'Ex: 50 micropieux Ø150mm, profondeur 12m...' : 'Ex: 50 micropiles Ø150mm, 12m depth...'} className="mt-2" rows={3} /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>{isFr ? 'Délai souhaité' : 'Timeline'} *</Label><Input required value={formData.delai_souhaite} onChange={(e) => setFormData({ ...formData, delai_souhaite: e.target.value })} placeholder={isFr ? 'Ex: Juin 2025' : 'Ex: June 2025'} className="mt-2" /></div>
                <div><Label>{isFr ? 'Budget approximatif' : 'Budget'}</Label><Input value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} placeholder="50 000€" className="mt-2" /></div>
              </div>
              <div className="pt-4 border-t">
                <div className="flex items-start gap-3">
                  <Checkbox id="rgpd" checked={formData.rgpd_accepted} onCheckedChange={(c) => setFormData({ ...formData, rgpd_accepted: c })} />
                  <Label htmlFor="rgpd" className="text-sm text-gray-600 cursor-pointer">{isFr ? "J'accepte le traitement de mes données (RGPD)" : "I accept data processing (GDPR)"}</Label>
                </div>
              </div>
              <Button type="submit" disabled={loading || !formData.rgpd_accepted} className="w-full bg-amber-500 hover:bg-amber-600 text-white py-4 text-lg">
                {loading ? <div className="flex items-center gap-2"><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div></div> : <span className="flex items-center gap-2">{isFr ? 'Demander un devis' : 'Request a quote'}<ArrowRight className="w-5 h-5" /></span>}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
